/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    getProductConfigure,
    IProductDimensionCallbacks,
    IProductDimensionConfigureResources,
    IProductDimensionConfigureViewProps
} from '@msdyn365-commerce/components';
import {
    ProductDimension,
    ProductDimensionType,
    ProductDimensionValue,
    ProductListsDataActions,
    ProductListType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import {
    addItemToProductListAction,
    AddItemToProductListInput,
    ArrayExtensions,
    createProductList,
    CreateProductListInput,
    FinitePromiseQueue,
    FinitePromiseQueueError,
    getDimensionsForSelectedVariant,
    GetDimensionsForSelectedVariantInput,
    getScanResult,
    GetScanResultInput,
    getSelectedVariant,
    IDimensionForSelectedVariant,
    IPromiseQueue,
    QuickOrderTemplate,
    QuickOrderTemplateLine,
    SelectedVariantInput,
    StringExtensions
} from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, Heading, IModuleProps, INodeProps, isMobile, ITelemetryContent, VariantType } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { debounce } from 'lodash';
import { computed } from 'mobx';
import * as React from 'react';

import { ActiveProductListInput } from './actions';
import {
    IQuickOrderGridActionsProps,
    IQuickOrderGridActionsViewProps,
    IQuickOrderGridRowCallbacks,
    IQuickOrderGridState,
    QuickOrderGridActionsView,
    QuickOrderGridDrawer,
    QuickOrderGridFormComponent,
    QuickOrderGridProduct,
    QuickOrderGridTableComponent } from './components';
import { IQuickOrderGridData } from './quick-order-grid.data';
import { IQuickOrderGridProps, IQuickOrderGridResources } from './quick-order-grid.props.autogenerated';

/**
 * QuickOrder Grid View props.
 */
export interface IQuickOrderGridViewProps extends IQuickOrderGridProps<IQuickOrderGridData> {
    header: JSX.Element;
    actions?: INodeProps;
    isB2bCustomer?: boolean;
    isQuickOrderGridFormExpanded: boolean;
    contentProps: INodeProps ;
    drawer: INodeProps;
    loading?: React.ReactNode;
    quickOrderGridForm: INodeProps;
    quickOrderGridFormProductContent: INodeProps;
    quickOrderGridFormMsgContent: INodeProps;
    quickOrderGridProps: IModuleProps;
    form: React.ReactNode;
    configure?: IProductDimensionConfigureViewProps;
    isMobileView: boolean;
    container?: JSX.Element;
    orderActions?: IQuickOrderGridActionsViewProps | null;
}

/**
 * QuickOrder Grid View port.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * QuickOrderGrid component.
 * @extends {React.PureComponent<IQuickOrderGridProps<IQuickOrderGridData>>}
 */
class QuickOrderGrid extends React.PureComponent<IQuickOrderGridProps<IQuickOrderGridData>, IQuickOrderGridState> {
    private readonly _productTextInput: React.RefObject<HTMLInputElement>;

    private readonly _quantityInput: React.RefObject<HTMLInputElement>;

    private readonly _waitTime: number = 500;

    private readonly _dimensionUpdateQueueMaxLength: number = 2;

    private readonly _errorCssClass: string = 'danger';

    private readonly _errorCssGlyphClass: string = 'fas fa-exclamation-triangle';

    private readonly _quickOrderGridFormClass: string;

    private readonly _quickOrderGridFormProductContent: string;

    private readonly _quickOrderGridFormMsgContent: string;

    private readonly _telemetryContent: ITelemetryContent;

    private readonly _dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(this._dimensionUpdateQueueMaxLength);

    private _dimensions: { [id: number]: string } = {};

    private readonly _cssClassNameConfigure: string = 'ms-quick-order_grid__content-form-main';

    private readonly _className: string = 'ms-quick-order-grid';

    // @ts-expect-error: Debouce needs to be used as type.
    private _cancellabelDebounce: debounce;

    private readonly _quickOrderGridCallbacks: IProductDimensionCallbacks = {

        /**
         * Get Dropdown name.
         * @param selectedDimensionId - Dimension type.
         * @param selectedDimensionValueId - Resources.
         * @returns Dropdown name.
         */
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this._dimensions[selectedDimensionId] = selectedDimensionValueId;
            return this._dimensionUpdateQueue.enqueue(async () => {
                return this._updateDimensions();
            }).catch((error: string) => { // Ignore discarded processes.
                if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                    throw new Error(error);
                }
            });
        },

        /**
         * Get Dropdown name.
         * @param dimensionType - Dimension type.
         * @param resources - Resources.
         * @returns Dropdown name.
         */
        getDropdownName: (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },

        /**
         * Method to call on dimension change.
         * @param isUpdatingDimension - Flag to update dimension.
         */
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        }
    };

    private readonly _quickOrderGridRowCallbacks: IQuickOrderGridRowCallbacks = {
        /**
         * Method to call remove button.
         * @param line - Grid row product.
         */
        onRemoveItem: (line: QuickOrderGridProduct): void => {
            this._removeItemRowFromTable(line.productLine);
        },

        /**
         * Method to call on change quantity button.
         * @param productListLineId - Grid row product list line id.
         * @param quantity - Product quantity.
         * @returns - Falg to get confirmation for action.
         */
        onChangeQuantity: (productListLineId: string | undefined, quantity: number): boolean => {
            return this._changeRowQuantity(productListLineId, quantity);
        }
    };

    public constructor(props: IQuickOrderGridProps<IQuickOrderGridData>) {
        super(props);
        this._productTextInput = React.createRef();
        this._quantityInput = React.createRef<HTMLInputElement>();

        this._telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!, this.props.friendlyName, this.props.telemetry);
        this.state = {
            isQuickOrderGridFormExpanded: false,
            quantity: 1,
            productResponse: undefined,
            productDimensions: undefined,
            productDimensionErrorState: {
                configureErrors: {}
            },
            isMobile: this.isMobile,
            products: [],
            isUpdatingDimension: false,
            productListId: ''
        };

        this._quickOrderGridFormClass = 'ms-quick-order-grid__content-form';
        this._quickOrderGridFormProductContent = 'ms-quick-order-grid__content-form-main';
        this._quickOrderGridFormMsgContent = 'ms-quick-order-grid__content-form-msg';
    }

    @computed public get isMobile(): boolean {
        const size = isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
        return size === 'xs';
    }

    public render(): JSX.Element | null {

        const { data } = this.props;
        if (!data.customerInformation.result) {
            return null;
        }
        const quickOrderTemplate = data.quickOrderTemplate;

        if (quickOrderTemplate?.result !== undefined) {
            this.setState({ products: quickOrderTemplate.result.quickOrderTemplateLines, productListId: quickOrderTemplate.result.productList.Id });
        }
        const { className = '', maxQuantity } = this.props.config;
        const customerInfo = this.props.data.customerInformation.result;
        const { quickOrderGridModuleHeading, quickOrderGridModuleDescription,
            quickOrderGridAddtogrid, quickOrderGridProductQuantityInputLabel,
            quickOrderGridProductInputLabel, quickOrderGridProductDimensionTypeColor,
            quickOrderGridProductDimensionTypeConfiguration, quickOrderGridSelectDimensionFormatString,
            quickOrderGridProductDimensionTypeSize, quickOrderGridProductDimensionTypeStyle,
            quickOrderGridProductDimensionTypeAmount, quickOrderGridModulePageHeading
        } = this.props.resources;

        const productConfigureResources: IProductDimensionConfigureResources = {
            selectDimensionFormatString: quickOrderGridSelectDimensionFormatString,
            productDimensionTypeColor: quickOrderGridProductDimensionTypeColor,
            productDimensionTypeConfiguration: quickOrderGridProductDimensionTypeConfiguration,
            productDimensionTypeSize: quickOrderGridProductDimensionTypeSize,
            productDimensionTypeStyle: quickOrderGridProductDimensionTypeStyle,
            productDimensionTypeAmount: quickOrderGridProductDimensionTypeAmount
        };

        const {
            isQuickOrderGridFormExpanded,
            productDimensionErrorState,
            productResponse,
            productDimensions
        } = this.state;

        const headerClassName = `${this._className}__header`;

        const header = (
            <div className={headerClassName}>
                <Heading
                    className={`${headerClassName}-heading`}
                    text={quickOrderGridModulePageHeading}
                />
            </div>
        );

        const viewProps: IQuickOrderGridViewProps = {
            ...(this.props as IQuickOrderGridProps<IQuickOrderGridData>),

            quickOrderGridProps: { className: classnames('ms-quick-order-grid', className), moduleProps: this.props },
            contentProps: { className: 'ms-quick-order-grid__content' },
            isB2bCustomer: customerInfo?.IsB2b,
            header,
            actions: {
                className: `${this._className}-action-bar`,
                tag: 'div'
            },
            drawer: {
                tag: QuickOrderGridDrawer,
                className: `${this._className}__drawer`,
                collapseProps: { isOpen: this.state.isQuickOrderGridFormExpanded },
                moduleHeadingText: quickOrderGridModuleHeading,
                moduleDescriptionText: quickOrderGridModuleDescription,
                requestContext: this.props.context.request,
                telemetryContent: this._telemetryContent
            },
            quickOrderGridForm: {
                className: `${this._quickOrderGridFormClass} ${isQuickOrderGridFormExpanded ? 'qo-show' : 'qo-hide'}`
            },
            quickOrderGridFormProductContent: {
                className: this._quickOrderGridFormProductContent
            },
            quickOrderGridFormMsgContent: {
                className: this._quickOrderGridFormMsgContent
            },
            form: QuickOrderGridFormComponent({
                productTextInput: this._productTextInput,
                quantityTextInput: this._quantityInput,
                isAddtoListDisabled: !productResponse,
                ariaLabelAddtoCart: quickOrderGridAddtogrid,
                ariaLabelProduct: quickOrderGridProductInputLabel,
                ariaLabelQuantity: quickOrderGridProductQuantityInputLabel,
                maxChars: 50,
                maxQuantity,
                moduleClassName: this._className,
                quickOrderGridState: this.state,
                handleInputChange: this._handleInputChange,
                handleQuantityChange: this._handleQuantityChange,
                handleAddProductListClick: this._addItemToProductListLine
            }),
            configure: productResponse && productDimensions ? getProductConfigure(this._cssClassNameConfigure, this.props.context, this.props.friendlyName,
                this.props.telemetry, quickOrderGridSelectDimensionFormatString,
                productConfigureResources, this._quickOrderGridCallbacks, productResponse,
                productDimensions, productDimensionErrorState) : undefined,
            isQuickOrderGridFormExpanded: this.state.isQuickOrderGridFormExpanded,
            isMobileView: this.state.isMobile,
            container: ArrayExtensions.hasElements(this.state.products) ? this._renderListContainer() : undefined,
            orderActions: this._renderQuickOrderGridActions(quickOrderTemplate?.result)
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderQuickOrderGridActions(quickOrderTemplate: QuickOrderTemplate | undefined): IQuickOrderGridActionsViewProps | null {
        const { typeName, context, resources } = this.props;
        const {
            quickOrderGridDeleteButtonText,
            quickOrderGridBuySelectedButtonText,
            quickOrderGridBuySelectedButtonAriaLabel,
            quickOrderGridBuySelectedAddingToCartErrorNotificationTitle,
            quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel,
            quickOrderGridGoToCartText,
            quickOrderGridDialogCloseText,
            quickOrderGridSingleItemText,
            quickOrderGridMultiLineItemFormatText,
            quickOrderGridMultiLinesFormatText,
            quickOrderGridHeaderMessageText,
            addedQuantityText,
            originalPriceText,
            currentPriceText,
            freePriceText
        } = resources;
        if (ArrayExtensions.hasElements(quickOrderTemplate?.quickOrderTemplateLines)) {
            const quickOrderGridActions: IQuickOrderGridActionsProps = {
                data: { quickOrderTemplate },
                moduleClassName: this._className,
                id: 'quick-order-grid',
                typeName,
                context,
                handlers: {
                    removeProductList: this._removeProductList
                },
                resources: {
                    quickOrderGridDeleteButtonText,
                    quickOrderGridBuySelectedButtonText,
                    quickOrderGridBuySelectedButtonAriaLabel,
                    quickOrderGridBuySelectedAddingToCartErrorNotificationTitle,
                    quickOrderGridBuySelectedAddingToCartErrorNotificationCloseAriaLabel,
                    quickOrderGridGoToCartText,
                    quickOrderGridDialogCloseText,
                    quickOrderGridSingleItemText,
                    quickOrderGridMultiLineItemFormatText,
                    quickOrderGridMultiLinesFormatText,
                    quickOrderGridHeaderMessageText,
                    addedQuantityText,
                    originalPriceText,
                    currentPriceText,
                    freePriceText
                }
            };

            return QuickOrderGridActionsView(quickOrderGridActions);
        }
        return null;
    }

    /**
     * Render list container.
     * @returns JSX element.
     */
    private _renderListContainer(): JSX.Element {
        const containerClass = `${this._className}__table-content`;
        return (
            <div className={containerClass}>
                {this._renderQuickOrderTable(this._className)}
            </div>
        );
    }

    /**
     * Initial the products on quick order.
     * @returns List of quick order products or undefined.
     */
    private _initQuickOrderGridProducts(): QuickOrderGridProduct[] {
        const products: QuickOrderTemplateLine[] = this.state.products;

        const quickOrderGridProduct: QuickOrderGridProduct[] = products.map((product: QuickOrderTemplateLine) => {
            return new QuickOrderGridProduct(product);
        });

        return ArrayExtensions.validValues(quickOrderGridProduct);
    }

    /**
     * Method to call on clear button.
     */
    private readonly _removeProductList = () => {
        const { context, data, telemetry, resources } = this.props;
        const { quickOrderTemplate } = data;
        const userToken = context.request.user.isAuthenticated && context.request.user.token;
        if (userToken && quickOrderTemplate && quickOrderTemplate.result) {
            ProductListsDataActions.deleteAsync({ callerContext: context.actionContext }, quickOrderTemplate.result.productList)
                .then(() => {
                    window.location.reload();
                })
                .catch((error: string) => {
                    telemetry.warning('[quick-order-grid] Unable to delete product list.');
                    telemetry.error(error);
                    this._updateStatusState(resources.quickOrderGridDeleteProductListFailedText, this._errorCssClass, this._errorCssGlyphClass, true);
                });
        } else {
            telemetry.warning('[quick-order-grid] Failed to delete product list. Product List was not found');
        }
    };

    /**
     * Initial the products on quick order.
     * @param className - Class name.
     * @returns JSX element.
     */
    private _renderQuickOrderTable(className: string): JSX.Element {
        const tableClassName = `${className}__table-list`;

        return (
            <QuickOrderGridTableComponent
                id={tableClassName}
                moduleId={this.props.id}
                moduleTypeName={this.props.typeName}
                typeName={this.props.typeName}
                key={tableClassName}
                className={tableClassName}
                data={{
                    quickOrderGridProducts: this._initQuickOrderGridProducts()
                }}
                context={this.props.context}
                resources={this.props.resources}
                isMobileView={this.state.isMobile}
                callBacks={this._quickOrderGridRowCallbacks}
            />
        );
    }

    /**
     * Method to called on product input change.
     * @param query - Product inventory info.
     */
    private readonly _handleInputChange = (query: string): void => {
        const queryMinLength = 4;
        if (!query || query.length > queryMinLength) {
            this.setState({ quantity: 0, productDimensions: [], productResponse: undefined });
            this._updateStatusState('', '', '', false);
        }
        if (this._cancellabelDebounce) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- existing code.
            this._cancellabelDebounce.cancel();
        }

        const {
            telemetry,
            context
        } = this.props;
        const zeroNumber = 0;
        if (!StringExtensions.isNullOrEmpty(query)) {
            this._cancellabelDebounce = debounce(() => {
                getScanResult(
                    new GetScanResultInput(
                        context.actionContext.requestContext.apiSettings,
                        query,
                        context.actionContext.requestContext.apiSettings.channelId
                    ),
                    this.props.context.actionContext)
                    .then(results => {
                        this.setState({
                            productResponse: results?.Product,
                            productDimensions: results?.ProductDimensionFull,
                            isQuickOrderGridFormExpanded: true
                        });
                    })
                    .catch((error: Error) => {
                        telemetry.exception(error);
                        telemetry.debug('Unable to get scan results');
                    });
            }, this._waitTime);

            setTimeout(() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- Existing code.
                this._cancellabelDebounce();
            }, zeroNumber);
            this.setState({ isQuickOrderGridFormExpanded: true });
        } else {
            this.setState({ productResponse: undefined, isQuickOrderGridFormExpanded: true });
        }
    };

    /**
     * Method to called on quantity change.
     * @param value - Quantity value.
     */
    private readonly _handleQuantityChange = (value: string): void => {
        const currentValue = Number.parseInt((value), 10);
        const inputElement = this._quantityInput.current instanceof HTMLInputElement && this._quantityInput.current;
        const quantityChangeDebounceTimeout = 200;
        const minimumQuantity = 0;

        this.setState(
            { quantity: currentValue },
            () => {
                debounce(
                    () => {
                        if (!Number.isNaN(this.state.quantity) && !(this.state.quantity < minimumQuantity) && inputElement) {
                            inputElement.setAttribute('aria-valuenow', currentValue.toString());
                            inputElement.setAttribute('value', currentValue.toString());
                        }
                    },
                    quantityChangeDebounceTimeout)();
            }
        );
    };

    /**
     * Method to get dropdwon name.
     * @param dimensionType - Dimension Type.
     * @param resources - String resources.
     * @returns Dropdwon label.
     */
    private readonly _getDropdownName = (dimensionType: number, resources: IProductDimensionConfigureResources): string => {
        const isGiftCard = this.state.productResponse?.IsGiftCard;

        switch (dimensionType) {
            case ProductDimensionType.Color: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case ProductDimensionType.Configuration: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case ProductDimensionType.Size: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case ProductDimensionType.Style: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    /**
     * Method to update dimension and product in UI.
     */
    private readonly _updateDimensions = async (): Promise<void> => {
        const {
            context: {
                actionContext,
                request: {
                    apiSettings: {
                        channelId
                    }
                }
            }
        } = this.props;
        const { productDimensions, productResponse } = this.state;
        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);
        if (!productResponse || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this._dimensions };
        this.setState({ isUpdatingDimension: true });

        // eslint-disable-next-line no-console -- Testing.
        console.log(this.state.isUpdatingDimension);

        // Step 1: Update state to indicate which dimensions are selected
        if (this.state.productDimensionErrorState.otherError) {
            // eslint-disable-next-line react/no-access-state-in-setstate -- existing code.
            const clearOtherErrorState = { ...this.state.productDimensionErrorState };
            clearOtherErrorState.otherError = undefined;
            this.setState({ productDimensionErrorState: clearOtherErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const dimensionKey of Object.keys(dimensionsToUpdate)) {
            if (this.state.productDimensionErrorState.configureErrors[dimensionKey]) {
                // eslint-disable-next-line react/no-access-state-in-setstate -- existing code.
                const errorState = { ...this.state.productDimensionErrorState };
                errorState.configureErrors[dimensionKey] = undefined;

                this.setState({ productDimensionErrorState: errorState });
            }
        }

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions?.map(dimension => {
            return {
                DimensionTypeValue: dimension.DimensionTypeValue,
                DimensionValue: this._updateDimensionValue(dimension, dimensionsToUpdate[dimension.DimensionTypeValue]) ?? dimension.DimensionValue,
                ExtensionProperties: dimension.ExtensionProperties
            };
        }).filter(dimension => {
            return dimension.DimensionValue;
        });

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        // eslint-disable-next-line no-async-promise-executor -- existing code
        const selectedProduct = new Promise<SimpleProduct | null>(async (resolve) => {
            const newProduct = (await getSelectedVariant(
                new SelectedVariantInput(
                    productResponse.MasterProductId ? productResponse.MasterProductId : productResponse.RecordId,
                    channelId,
                    mappedDimensions
                ),
                actionContext
            ));
            if (newProduct) {
                await getDimensionsForSelectedVariant(
                    new GetDimensionsForSelectedVariantInput(
                        newProduct.MasterProductId ? newProduct.MasterProductId : newProduct.RecordId,
                        channelId,
                        mappedDimensions
                    ),
                    actionContext
                );
            }

            resolve(newProduct);
        });

        const variantProduct = await selectedProduct;
        this.setState({ productResponse: variantProduct !== null ? variantProduct : undefined });
    };

    /**
     * Method to get updated dimension value.
     * @param productDimensionFull - Product dimension full.
     * @param newValueId - New Dimension value.
     * @returns Dimension Value.
     */
    private readonly _updateDimensionValue = (productDimensionFull: IDimensionForSelectedVariant,
        newValueId: string | undefined): ProductDimensionValue | undefined => {
        if (newValueId && productDimensionFull.DimensionValues) {
            return productDimensionFull.DimensionValues.find(dimension => dimension.RecordId === +newValueId);
        }

        return undefined;
    };

    /**
     * Method to get dropdwon name.
     * @param dimensionTypeValue - Dimension Type.
     * @param resources - String resources.
     * @param isGiftCard - Flag to check if its a giftcard product.
     * @returns Dropdwon error label.
     */
    private readonly _getDimensionErrorString = (dimensionTypeValue: number, resources: IQuickOrderGridResources, isGiftCard?: boolean): string => {
        const amountErrorMessage = resources.quickOrderGridproductDimensionTypeAmountErrorMessage;

        switch (dimensionTypeValue) {
            case ProductDimensionType.Color: // ProductDimensionType.Color
                return resources.quickOrderGridproductDimensionTypeColorErrorMessage;
            case ProductDimensionType.Configuration: // ProductDimensionType.Configuration
                return resources.quickOrderGridproductDimensionTypeConfigurationErrorMessage;
            case ProductDimensionType.Size: // ProductDimensionType.Size
                return resources.quickOrderGridproductDimensionTypeSizeErrorMessage;
            case ProductDimensionType.Style: // ProductDimensionType.Style
                return isGiftCard ? amountErrorMessage : resources.quickOrderGridproductDimensionTypeStyleErrorMessage;
            default:
                return '';
        }
    };

    /**
     * Initial the products on quick order.
     * @param productResponse - Simple Product.
     * @param quantity - Product Quantity.
     * @returns Flag to validate productlistline.
     */
    private readonly _validateCart = (productResponse: SimpleProduct, quantity: number): boolean => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain -- existing code
        const missingDimensions = productResponse.Dimensions?.filter(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value));
        const minimumQuantity = 0;
        if (ArrayExtensions.hasElements(missingDimensions)) {
            const userMessage = this._getConfigureErrors(missingDimensions, false);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- existing code
            if (this.state.productDimensionErrorState.configureErrors) {
                // eslint-disable-next-line react/no-access-state-in-setstate -- existing code
                const errorState = { ...this.state.productDimensionErrorState };
                errorState.configureErrors = userMessage;

                this.setState({ productDimensionErrorState: errorState });
                return false;
            }
        }

        if (quantity <= minimumQuantity) {
            const resources = this.props.resources;
            this._updateStatusState(resources.quickOrderGridWrongQuantityErrorMsg, this._errorCssClass, this._errorCssGlyphClass, true);
            return false;
        }
        return true;
    };

    /**
     * Initial the products on quick order.
     * @param result - Simple Product.
     * @param isGiftCard - Product Quantity.
     * @returns Flag to validate cart.
     */
    private _getConfigureErrors(result: ProductDimension[] | undefined, isGiftCard?: boolean): { [configureId: string]: string | undefined } {
        if (!result) {
            return {};
        }
        const dimensions: { [configureId: string]: string | undefined } = {};
        const resources = this.props.resources;
        for (const dimension of result) {
            dimensions[dimension.DimensionTypeValue] = this._getDimensionErrorString(dimension.DimensionTypeValue, resources, isGiftCard);
        }
        return dimensions;
    }

    /**
     * Method to call on quantity change in row.
     * @param itemKey - Product Key.
     * @param quantity - Product quantity.
     * @returns Flag to update status.
     */
    private readonly _changeRowQuantity = (itemKey: string | undefined, quantity: number): boolean => {
        const { context, data, telemetry } = this.props;
        const { quickOrderTemplate } = data;
        if (quickOrderTemplate?.result) {
            const quickOrderTemplateLinesData = quickOrderTemplate.result.quickOrderTemplateLines;
            if (ArrayExtensions.hasElements(quickOrderTemplateLinesData)) {
                const orderLineIndex = quickOrderTemplateLinesData.findIndex((lines: QuickOrderTemplateLine) => lines.productListLine.LineId === itemKey);
                const newProductListLine = { ...quickOrderTemplateLinesData[orderLineIndex] };
                if (quickOrderTemplateLinesData[orderLineIndex].productListLine.Quantity === quantity) {
                    return true;
                }
                const previousQuantity = newProductListLine.productListLine.Quantity;
                newProductListLine.productListLine.Quantity = quantity;
                const { fullProduct, ...line } = newProductListLine;

                ProductListsDataActions.updateProductListLinesAsync({ callerContext: context.actionContext }, quickOrderTemplate.result.productList.Id,
                    [line.productListLine])
                    .then(list => {
                        if (ArrayExtensions.hasElements(list)) {
                            const newLine = list.find(lines => lines.LineId === itemKey);
                            quickOrderTemplateLinesData[orderLineIndex].productListLine = newLine!;
                            const updatedLines = [...quickOrderTemplateLinesData];
                            updatedLines[orderLineIndex] = { fullProduct, productListLine: newLine! };
                            const updatedProductList = { ...quickOrderTemplate.result, ProductListLines: updatedLines };
                            context.actionContext.update(new ActiveProductListInput(context.actionContext.requestContext), updatedProductList);
                            return true;
                        }

                        return false;
                    })
                    .catch((error: Error) => {
                        telemetry.warning('[quick-order-grid] Unable to update product quantity.');
                        telemetry.error(error.message);
                        const userMessage = 'Failed to update quantity value.';
                        newProductListLine.productListLine.Quantity = previousQuantity;
                        this._updateStatusState(userMessage, this._errorCssClass, this._errorCssGlyphClass, true);
                        return false;
                    });
            }
            return false;
        }

        return false;
    };

    /**
     * Method to call on remove item from table.
     * @param line - Order Template Line.
     */
    private readonly _removeItemRowFromTable = (line: QuickOrderTemplateLine): void => {
        const { context, data, telemetry } = this.props;
        const { quickOrderTemplate } = data;

        if (quickOrderTemplate?.result) {
            const userToken = context.request.user.isAuthenticated && context.request.user.token;
            if (userToken) {
                ProductListsDataActions.removeProductListLinesAsync({ callerContext: context.actionContext },
                    line.productListLine.ProductListId!, [line.productListLine])
                    .then(() => {
                        window.location.reload(false);
                    })
                    .catch((error: Error) => {
                        telemetry.warning('[quick-order-grid] Unable remove item from product list grid');
                        telemetry.error(error.message);
                    });
            } else {
                telemetry.warning(
                    '[quick-order-grid] Unable remove items from product list grid user token was not found'
                );
            }
        }
    };

    /**
     * Method to call on add item from form.
     */
    private readonly _addItemToProductListLine = async (): Promise<void> => {
        const { context: { actionContext }, resources, telemetry } = this.props;
        const { quickOrderGridAddedToGridSuccessMessage, quickOrderGridAddedToGridFailureMessage, quickOrderGridAddedToGridDuplicateError } = resources;
        const { productResponse, quantity, productListId } = this.state;
        let newProductList = '';

        this._updateStatusState('', '', '', false);

        if (productResponse !== undefined && this._validateCart(productResponse, quantity)) {
            if (StringExtensions.isNullOrWhitespace(productListId)) {
                newProductList = this._createProductList();
            } else {
                newProductList = this.state.productListId;
            }

            if (StringExtensions.isNullOrWhitespace(newProductList)) {
                this._updateStatusState(quickOrderGridAddedToGridFailureMessage, this._errorCssClass, this._errorCssGlyphClass, true);
                return;
            }

            const input = new AddItemToProductListInput(
                newProductList,
                productResponse.RecordId,
                quantity,
                productResponse.DefaultUnitOfMeasure ?? 'ea'
            );

            await addItemToProductListAction(input, actionContext)
                .then(result => {
                    if (result.ProductListId) {
                        this._updateStatusState(quickOrderGridAddedToGridSuccessMessage, 'success', 'fas fa-check', true);
                        window.location.reload();
                        this.setState({});
                    }
                })
                .catch((error: Error) => {
                    telemetry.debug('[quick-order-grid] Error trying to Update Grid Line from the quick order grid');
                    if (error.name === 'Microsoft_Dynamics_Commerce_Runtime_DuplicateObject') {
                        this._updateStatusState(quickOrderGridAddedToGridDuplicateError, this._errorCssClass, this._errorCssGlyphClass, true);

                    } else {
                        this._updateStatusState(quickOrderGridAddedToGridFailureMessage, this._errorCssClass, this._errorCssGlyphClass, true);
                    }
                });
        }
    };

    /**
     * Method to create product list.
     * @returns - ProductListId.
     */
    private readonly _createProductList = (): string => {
        const { context: { actionContext }, telemetry, resources } = this.props;
        const { productListId } = this.state;
        const { quickOrderGridAddedToGridFailureMessage } = resources;

        if (StringExtensions.isNullOrWhitespace(productListId)) {
            const input = new CreateProductListInput('', ProductListType.QuickOrder);

            createProductList(input, actionContext)
                .then(result => {
                    if (!StringExtensions.isNullOrWhitespace(result.Id)) {
                        this.setState({ productListId: result.Id });
                        return result.Id;
                    }
                    return '';
                })
                .catch((error: Error) => {
                    telemetry.debug('[quick-order-grid] Error on creating productlist from quick order grid');
                    telemetry.error(error.message);
                    this._updateStatusState(quickOrderGridAddedToGridFailureMessage, this._errorCssClass, this._errorCssGlyphClass, true);
                    return '';
                });
            return '';
        }

        return productListId;
    };

    /**
     * Method to create product list.
     * @param messageText - Message Text.
     * @param statusCssString - Css class names.
     * @param messageGlyph - Glyph string.
     * @param isOpen - Flag to show.
     */
    private readonly _updateStatusState = (messageText: string, statusCssString: string, messageGlyph: string, isOpen: boolean): void => {
        this.setState(
            {
                // eslint-disable-next-line react/no-unused-state -- Showing user message in Form component.
                stateMessage: {
                    messageText,
                    statusCssString,
                    messageGlyph,
                    isOpen
                }
            });
    };
}
export default QuickOrderGrid;

